import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import * as XLSX from 'xlsx'

const LIEU_DE_VIE_ENUM = [
    'Logement',
    'Rue',
    'MA',
    'MRS',
    'SHNA',
    'Centre de cure',
    'Centre post-cure',
    'Famille/Ami',
    "Hébergement d'urgence",
    'Hôpital général',
    'Hôpital psychiatrique',
    'IHP',
    'MSP',
    'Prison',
    'Squat',
]

interface BeneficiaryRaw {
    id: string
    sexe: string | null
    date_naissance: string | null
    date_premiere_convention: string | null
    date_suspension_termination: string | null
    lieu_vie_type: string | null
    is_active: boolean | null
}

interface PitSnapshot {
    label: string
    isCurrent: boolean
    suivis: number
    femmes: number
    hommes: number
    lieuDeVie: Record<string, number>
    ageMoyen: number | null
    maintienLogement: number | null
}

function ageAt(dateNaissance: string, atDate: Date): number {
    const birth = new Date(dateNaissance)
    let age = atDate.getFullYear() - birth.getFullYear()
    const m = atDate.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && atDate.getDate() < birth.getDate())) age--
    return age
}

function isActiveAt(b: BeneficiaryRaw, date: Date): boolean {
    if (!b.date_premiere_convention) return false
    const conv = new Date(b.date_premiere_convention)
    if (conv > date) return false
    if (b.date_suspension_termination) {
        if (new Date(b.date_suspension_termination) <= date) return false
    }
    return true
}

function getQuarterlyDates(earliest: Date): Date[] {
    const qMonth = Math.floor(earliest.getMonth() / 3) * 3
    let d = new Date(earliest.getFullYear(), qMonth, 1)
    const today = new Date()
    const dates: Date[] = []
    while (d <= today) {
        dates.push(new Date(d))
        d = new Date(d.getFullYear(), d.getMonth() + 3, 1)
    }
    return dates
}

function computeSnapshot(benefs: BeneficiaryRaw[], date: Date, isCurrent: boolean, label: string): PitSnapshot {
    const active = isCurrent
        ? benefs.filter(b => b.is_active !== false && b.date_premiere_convention)
        : benefs.filter(b => isActiveAt(b, date))

    const suivis = active.length
    const femmes = active.filter(b => b.sexe === 'Femme').length
    const hommes = active.filter(b => b.sexe === 'Homme').length

    const lieuDeVie: Record<string, number> = {}
    for (const val of LIEU_DE_VIE_ENUM) {
        lieuDeVie[val] = active.filter(b => b.lieu_vie_type === val).length
    }

    const withAge = active.filter(b => b.date_naissance)
    const ageMoyen = withAge.length > 0
        ? Math.round(withAge.reduce((s, b) => s + ageAt(b.date_naissance!, date), 0) / withAge.length)
        : null

    const logement = lieuDeVie['Logement'] ?? 0
    const maintienLogement = suivis > 0 ? (logement / suivis) * 100 : null

    return { label, isCurrent, suivis, femmes, hommes, lieuDeVie, ageMoyen, maintienLogement }
}

function fmtPct(v: number | null): string {
    if (v === null) return '—'
    return v.toFixed(1) + '%'
}

function exportToExcel(current: PitSnapshot, historical: PitSnapshot[], visibleLieux: string[]) {
    const headers = ['STATISTIQUES', 'PIT', '%', ...historical.map(s => s.label)]

    const rows: (string | number)[][] = [
        ['Suivis', current.suivis, '/', ...historical.map(s => s.suivis)],
        ['Femmes', current.femmes, current.suivis > 0 ? ((current.femmes / current.suivis) * 100).toFixed(1) + '%' : '/', ...historical.map(s => s.femmes)],
        ['Hommes', current.hommes, current.suivis > 0 ? ((current.hommes / current.suivis) * 100).toFixed(1) + '%' : '/', ...historical.map(s => s.hommes)],
        ...visibleLieux.map(lieu => {
            const val = current.lieuDeVie[lieu] ?? 0
            const pct = current.suivis > 0 ? ((val / current.suivis) * 100).toFixed(1) + '%' : '/'
            return [lieu, val, pct, ...historical.map(s => s.lieuDeVie[lieu] ?? 0)]
        }),
        ['Âge moyen', current.ageMoyen ?? '—', '/', ...historical.map(s => s.ageMoyen ?? '—')],
        ['Maintien en logement', fmtPct(current.maintienLogement), '/', ...historical.map(s => fmtPct(s.maintienLogement))],
    ]

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    ws['!cols'] = headers.map((_, i) => ({ wch: i === 0 ? 26 : 10 }))

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'PIT Stats')
    XLSX.writeFile(wb, `PIT_Stats_${new Date().toISOString().split('T')[0]}.xlsx`)
}

export const PitStatsTable = () => {
    const [current, setCurrent] = useState<PitSnapshot | null>(null)
    const [historical, setHistorical] = useState<PitSnapshot[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const load = async () => {
            const { data } = await supabase.from('beneficiaries').select(
                'id, sexe, date_naissance, date_premiere_convention, date_suspension_termination, lieu_vie_type, is_active'
            )
            if (!data || data.length === 0) { setLoading(false); return }

            const benefs = data as BeneficiaryRaw[]
            const today = new Date()

            const earliest = benefs
                .map(b => b.date_premiere_convention)
                .filter(Boolean)
                .sort()[0]

            if (!earliest) { setLoading(false); return }

            const quarterDates = getQuarterlyDates(new Date(earliest))

            setCurrent(computeSnapshot(benefs, today, true, 'PIT'))
            setHistorical(quarterDates
                .filter(d => d.getFullYear() >= 2023)
                .map(d =>
                    computeSnapshot(benefs, d, false,
                        d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                    )
                )
            )
            setLoading(false)
        }
        load()
    }, [])

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques PIT</h3>
                <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            </div>
        )
    }

    if (!current) return null

    const allSnapshots = [current, ...historical]

    // Only show lieu de vie rows that have at least one non-zero count across all periods
    const visibleLieux = LIEU_DE_VIE_ENUM.filter(lieu =>
        allSnapshots.some(s => (s.lieuDeVie[lieu] ?? 0) > 0)
    )

    const renderVal = (val: number | null, isPercent?: boolean) =>
        val !== null ? (isPercent ? fmtPct(val) : val) : '—'

    const renderPct = (val: number | null, total: number) =>
        val !== null && total > 0 ? fmtPct((val / total) * 100) : '/'

    return (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-gray-900">Statistiques PIT</h3>
                <button
                    onClick={() => exportToExcel(current, historical, visibleLieux)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                >
                    ⬇ Excel
                </button>
            </div>
            <p className="text-xs text-gray-400 mb-4">
                Suivis & âge : calcul historique exact. Situation résidentielle : état actuel projeté sur les périodes passées (approximation).
            </p>
            <div className="overflow-x-auto">
                <table className="text-sm border-collapse w-full">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 border border-gray-200 sticky left-0 bg-gray-100 z-10 w-[180px]">
                                STATISTIQUES
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-bold text-blue-700 border border-gray-200 bg-blue-50">
                                PIT
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-bold text-blue-700 border border-gray-200 bg-blue-50">
                                %
                            </th>
                            {historical.map((s, i) => (
                                <th key={i} className="px-2 py-1 text-center text-[10px] font-semibold text-gray-500 border border-gray-200 whitespace-pre-wrap leading-tight">
                                    {'PIT\n' + s.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {/* Fixed rows: Suivis / Femmes / Hommes */}
                        {[
                            { label: 'Suivis', val: current.suivis, noPct: true },
                            { label: 'Femmes', val: current.femmes },
                            { label: 'Hommes', val: current.hommes },
                        ].map(({ label, val, noPct }) => (
                            <tr key={label} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5 font-medium text-gray-800 border border-gray-200 sticky left-0 bg-white whitespace-nowrap text-xs">{label}</td>
                                <td className="px-3 py-1.5 text-center font-semibold text-blue-700 border border-gray-200 bg-blue-50 text-xs">{val}</td>
                                <td className="px-3 py-1.5 text-center text-gray-600 border border-gray-200 bg-blue-50 text-xs">
                                    {noPct ? '/' : renderPct(val, current.suivis)}
                                </td>
                                {historical.map((snap, j) => {
                                    const hVal = label === 'Suivis' ? snap.suivis : label === 'Femmes' ? snap.femmes : snap.hommes
                                    return <td key={j} className="px-2 py-1.5 text-center text-gray-700 border border-gray-200 text-xs">{hVal}</td>
                                })}
                            </tr>
                        ))}

                        {/* Separator */}
                        <tr><td colSpan={3 + historical.length} className="h-2 bg-gray-50 border-y border-gray-100" /></tr>

                        {/* Dynamic lieu de vie rows */}
                        {visibleLieux.map(lieu => {
                            const val = current.lieuDeVie[lieu] ?? 0
                            return (
                                <tr key={lieu} className="hover:bg-gray-50">
                                    <td className="px-3 py-1.5 font-medium text-gray-800 border border-gray-200 sticky left-0 bg-white whitespace-nowrap text-xs">{lieu}</td>
                                    <td className="px-3 py-1.5 text-center font-semibold text-blue-700 border border-gray-200 bg-blue-50 text-xs">{val}</td>
                                    <td className="px-3 py-1.5 text-center text-gray-600 border border-gray-200 bg-blue-50 text-xs">
                                        {renderPct(val, current.suivis)}
                                    </td>
                                    {historical.map((snap, j) => (
                                        <td key={j} className="px-2 py-1.5 text-center text-gray-700 border border-gray-200 text-xs">
                                            {snap.lieuDeVie[lieu] ?? 0}
                                        </td>
                                    ))}
                                </tr>
                            )
                        })}

                        {/* Separator */}
                        <tr><td colSpan={3 + historical.length} className="h-2 bg-gray-50 border-y border-gray-100" /></tr>

                        {/* Âge moyen */}
                        <tr className="hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-medium text-gray-800 border border-gray-200 sticky left-0 bg-white whitespace-nowrap text-xs">Âge moyen</td>
                            <td className="px-3 py-1.5 text-center font-semibold text-blue-700 border border-gray-200 bg-blue-50 text-xs">{renderVal(current.ageMoyen)}</td>
                            <td className="px-3 py-1.5 text-center text-gray-600 border border-gray-200 bg-blue-50 text-xs">/</td>
                            {historical.map((snap, j) => (
                                <td key={j} className="px-2 py-1.5 text-center text-gray-700 border border-gray-200 text-xs">{renderVal(snap.ageMoyen)}</td>
                            ))}
                        </tr>

                        {/* Maintien en logement */}
                        <tr className="hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-medium text-gray-800 border border-gray-200 sticky left-0 bg-white whitespace-nowrap text-xs">Maintien en logement</td>
                            <td className="px-3 py-1.5 text-center font-semibold text-blue-700 border border-gray-200 bg-blue-50 text-xs">{fmtPct(current.maintienLogement)}</td>
                            <td className="px-3 py-1.5 text-center text-gray-600 border border-gray-200 bg-blue-50 text-xs">/</td>
                            {historical.map((snap, j) => (
                                <td key={j} className="px-2 py-1.5 text-center text-gray-700 border border-gray-200 text-xs">{fmtPct(snap.maintienLogement)}</td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    )
}
